import InfoIcon from "@mui/icons-material/Info";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  Tooltip,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import AutocompleteWithQuery from "components/AutocompleteWithQuery";
import ContextOrganizationField from "components/ContextOrganizationField";
import RedirectBackButton from "components/RedirectBackButton";
import { addDays, compareAsc, isValid } from "date-fns";
import useGlobalContext from "hooks/globalData";
import useTitle from "hooks/useTitle";
import { useEffect, useRef, useState } from "react";
import { Field, FieldInputProps, withTypes } from "react-final-form";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery } from "react-query";
import {
  AuthorityFilter,
  ListRequestOfAuthorityFilter,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import { standardDateFormat } from "utils/dateUtils";
import SubmissionReportsPreviewTable from "./SubmissionReportsPreviewTable";

export type SubmissionReportsFormValues = {
  organizationId?: string;
  authorityId?: string;
  startDate?: Date;
  endDate?: Date;
  newEntriesOnly: boolean;
  removeInternalUsers: boolean;
};

const { Form } = withTypes<SubmissionReportsFormValues>();

const styles = {
  checkboxContainer: {
    display: "flex",
    alignItems: "center",
  },
  formControlLabel: {
    marginRight: 0,
  },
  previewContainer: {
    margin: 2,
    width: "100%",
  },
  continueButton: {
    marginTop: 1,
  },
};

const mustBeDate = (value: any) =>
  !isValid(value) ? "Must be a date" : undefined;

const endDateShouldBeAfterStartDate = (
  startDate: Date | undefined,
  endDate: Date | undefined
) =>
  isValid(startDate) &&
  isValid(endDate) &&
  compareAsc(startDate!, endDate!) === -1
    ? undefined
    : `End date should be after start date`;

const AddCreditSubmissionReport = () => {
  const [isPreviewVisible, setPreviewVisible] = useState(false);
  const { formatMessage: t } = useIntl();
  const { authorityService, previouslyExportedReportsUrl } = useGlobalContext();
  const { organizationId } = contextStore.useState();

  useTitle(t({ id: "exportCreditSubmissionReportTitle" }));

  const fetchedAuthorities = useQuery(["getAuthorities", organizationId], () =>
    authorityService.getAuthorities(
      ListRequestOfAuthorityFilter.fromJS({
        parameters: AuthorityFilter.fromJS({
          manualSubmissionSettingValue: true,
        }),
      })
    )
  );

  const previousOrganizationId = useRef(organizationId);

  useEffect(() => {
    if (previousOrganizationId.current === organizationId) {
      return;
    }

    previousOrganizationId.current = organizationId;
    setPreviewVisible(false);
  }, [organizationId]);

  const onSubmit = () => {
    if (!isPreviewVisible) {
      setPreviewVisible(true);
    }
  };

  const onInputChange = (e: any, input: FieldInputProps<any, HTMLElement>) => {
    input.onChange(e);
    setPreviewVisible(false);
  };

  const noAuthorites =
    fetchedAuthorities.isFetched && fetchedAuthorities.data?.data?.length === 0;

  return (
    <>
      <Box mb={3}>
        <RedirectBackButton
          redirectRoute={previouslyExportedReportsUrl()}
          labelKey="backToListButtonLabel"
        />
      </Box>
      <Form
        key={organizationId}
        onSubmit={onSubmit}
        render={({ handleSubmit, values, submitting, form: { change } }) => {
          const startDateValidationError = mustBeDate(values.startDate);
          const endDateValidationError =
            mustBeDate(values.endDate) ||
            endDateShouldBeAfterStartDate(values.startDate, values.endDate);

          const isModelValid =
            !!values.authorityId &&
            !startDateValidationError &&
            !endDateValidationError;

          return (
            <form onSubmit={handleSubmit}>
              <Grid container justifyContent="center">
                <Grid item xs={12} sm={8} md={6}>
                  <Typography variant="h4">
                    <T id="creditSubmissionReports" />
                  </Typography>
                  <Grid container spacing={2} mt={1}>
                    <ContextOrganizationField
                      values={values}
                      formChange={change}
                    />
                    <Field name="authorityId" required>
                      {({ input, meta }) => (
                        <Grid item xs={12}>
                          <FormControl
                            fullWidth
                            error={meta.error && meta.submitFailed}
                          >
                            <Tooltip
                              title={
                                noAuthorites &&
                                t({ id: "noCreditSubmissionReportsConfigured" })
                              }
                            >
                              <div>
                                <AutocompleteWithQuery
                                  required
                                  {...input}
                                  disabled={noAuthorites}
                                  labelField="authorityTypeName"
                                  label={t({
                                    id: "approvingAuthorityTypeLabel",
                                  })}
                                  fetchedData={fetchedAuthorities}
                                  failMessage={t({ id: "selectFetchFailed" })}
                                  onChange={(e) => onInputChange(e, input)}
                                />
                              </div>
                            </Tooltip>
                            {meta.error && meta.submitFailed && (
                              <FormHelperText>{meta.error}</FormHelperText>
                            )}
                          </FormControl>
                        </Grid>
                      )}
                    </Field>

                    <Field name="startDate" required>
                      {({ input, meta }) => (
                        <Grid item xs={12}>
                          <DatePicker
                            maxDate={
                              isValid(values.endDate)
                                ? addDays(values.endDate!, -1)
                                : null
                            }
                            label={t({ id: "certificateIssuedStartDateLabel" })}
                            format={standardDateFormat}
                            {...input}
                            value={input.value === "" ? null : input.value}
                            slotProps={{
                              textField: {
                                required: true,
                                fullWidth: true,
                                variant: "standard",
                                error:
                                  !meta.pristine && !!startDateValidationError,
                                helperText:
                                  !meta.pristine && startDateValidationError,
                              },
                              actionBar: {
                                actions: ["clear", "cancel"],
                              },
                            }}
                            onChange={(e) => onInputChange(e, input)}
                          />
                        </Grid>
                      )}
                    </Field>
                    <Field name="endDate" required>
                      {({ input, meta }) => (
                        <Grid item xs={12}>
                          <DatePicker
                            minDate={
                              isValid(values.startDate)
                                ? addDays(values.startDate!, 1)
                                : null
                            }
                            disabled={!isValid(values.startDate)}
                            label={t({ id: "certificateIssuedEndDateLabel" })}
                            format={standardDateFormat}
                            {...input}
                            value={input.value === "" ? null : input.value}
                            slotProps={{
                              textField: {
                                required: true,
                                variant: "standard",
                                fullWidth: true,
                                error:
                                  !meta.pristine && !!startDateValidationError,
                                helperText:
                                  !meta.pristine && startDateValidationError,
                              },
                              actionBar: {
                                actions: ["clear", "cancel"],
                              },
                            }}
                            onChange={(e) => onInputChange(e, input)}
                          />
                        </Grid>
                      )}
                    </Field>
                    <Field name="newEntriesOnly" type="checkbox" required>
                      {({ input }) => (
                        <Grid item xs={12}>
                          <Box sx={styles.checkboxContainer} gap={1}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  {...input}
                                  onChange={(e) => onInputChange(e, input)}
                                />
                              }
                              label={<T id="exportOnlyNewEntriesLabel" />}
                              sx={styles.formControlLabel}
                            />
                            <Tooltip
                              title={t({
                                id: "exportOnlyNewEntriesInfoMessage",
                              })}
                            >
                              <InfoIcon />
                            </Tooltip>
                          </Box>
                        </Grid>
                      )}
                    </Field>
                    <Field name="removeInternalUsers" type="checkbox">
                      {({ input }) => (
                        <Grid item xs={12}>
                          <Box sx={styles.checkboxContainer} gap={1}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  {...input}
                                  onChange={(e) => onInputChange(e, input)}
                                />
                              }
                              label={<T id="removeInternalUsersLabel" />}
                              sx={styles.formControlLabel}
                            />
                            <Tooltip
                              title={t({
                                id: "removeInternalUsersInfoMessage",
                              })}
                            >
                              <InfoIcon />
                            </Tooltip>
                          </Box>
                        </Grid>
                      )}
                    </Field>
                  </Grid>
                </Grid>

                {isPreviewVisible && isModelValid && (
                  <Box sx={styles.previewContainer}>
                    <SubmissionReportsPreviewTable {...values} />
                  </Box>
                )}

                <Grid container alignItems="center" direction="column">
                  <Grid item xs={12}>
                    {!isPreviewVisible && (
                      <Button
                        disabled={submitting || !isModelValid}
                        type="submit"
                        color="primary"
                        sx={styles.continueButton}
                        variant="outlined"
                      >
                        <T id="continueButton" />
                      </Button>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </form>
          );
        }}
      />
    </>
  );
};

export default AddCreditSubmissionReport;
